/**
 * @ipc/core · src/state-machine.ts
 *
 * Единый статус-автомат платежа на нейтральном наборе PaymentStatus.
 * Стартовое состояние при создании — CREATED. Таблица разрешённых переходов —
 * дословно из tech-spec/03-core-orchestrator.md (раздел «Статус-автомат платежа»).
 */

import { PaymentStatus } from './status.ts';

/** Разрешённые переходы: from → допустимые to. */
export const ALLOWED_TRANSITIONS: Readonly<Record<PaymentStatus, readonly PaymentStatus[]>> = {
  [PaymentStatus.CREATED]: [
    PaymentStatus.PENDING,
    PaymentStatus.AUTHORIZED,
    PaymentStatus.CONFIRMED,
    PaymentStatus.FAILED,
    PaymentStatus.CANCELLED,
  ],
  [PaymentStatus.PENDING]: [
    PaymentStatus.AUTHORIZED,
    PaymentStatus.CONFIRMED,
    PaymentStatus.FAILED,
    PaymentStatus.CANCELLED,
  ],
  [PaymentStatus.AUTHORIZED]: [PaymentStatus.CONFIRMED, PaymentStatus.FAILED, PaymentStatus.CANCELLED],
  [PaymentStatus.CONFIRMED]: [PaymentStatus.REFUNDED, PaymentStatus.PARTIALLY_REFUNDED],
  // PARTIALLY_REFUNDED → PARTIALLY_REFUNDED НЕ в таблице: canTransition(from===to) = false,
  // добор partial-refund — новая запись Refund, статус платежа не «переоткрывается» в себя.
  [PaymentStatus.PARTIALLY_REFUNDED]: [PaymentStatus.REFUNDED],
  [PaymentStatus.FAILED]: [],
  [PaymentStatus.REFUNDED]: [],
  [PaymentStatus.CANCELLED]: [],
};

export class IllegalTransitionError extends Error {
  readonly from: PaymentStatus;
  readonly to: PaymentStatus;

  constructor(from: PaymentStatus, to: PaymentStatus) {
    super(`Illegal payment transition: ${from} -> ${to}`);
    this.name = 'IllegalTransitionError';
    this.from = from;
    this.to = to;
  }
}

/**
 * true, если переход from → to разрешён таблицей ALLOWED_TRANSITIONS.
 * from === to всегда false: доборы PARTIALLY_REFUNDED → PARTIALLY_REFUNDED
 * обрабатываются на уровне команды partialRefund оркестратора (новая запись Refund,
 * статус платежа не переоткрывается через автомат), а не повторным переходом в себя.
 */
export function canTransition(from: PaymentStatus, to: PaymentStatus): boolean {
  if (from === to) {
    return false;
  }
  return ALLOWED_TRANSITIONS[from].includes(to);
}

/** Возвращает целевой статус, если переход разрешён; иначе бросает IllegalTransitionError. */
export function assertTransition(from: PaymentStatus, to: PaymentStatus): PaymentStatus {
  if (!canTransition(from, to)) {
    throw new IllegalTransitionError(from, to);
  }
  return to;
}
